import { useMemo, useCallback } from "react"
import { map, chain, keys, keyBy, capitalize, find, toUpper, compact } from "lodash"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import { UUID, ROLE, TOKEN } from "helpers/shapes"
import { TEAM_ROLES, isAllAccessTeam } from "helpers/permissions/teams"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import Table from "components/material/table"
import { TEAM_TOKEN } from "./shapes"

export default function TeamTokens({ account, teamId, teamAlias, role, teamRole, tokens, teamTokens, tabs, update }) {
  const hasWriteAccess = useMemo(() => role === "admin" || toUpper(teamRole) === "ADMIN", [role, teamRole])
  const tokensById = useMemo(() => keyBy(tokens, "id"), [tokens])

  const optionLabels = useMemo(() => chain(tokensById)
    .mapValues(({ name }) => name)
    .omitBy((_, id) => find(teamTokens, t => t.id === id))
    .value(),
  [teamTokens, tokensById])

  // There are no Team Admins for the All access team
  const roleOptions = compact(["MEMBER", !isAllAccessTeam(teamAlias) && "ADMIN"])

  const addForm = useMemo(() => {
    if (!hasWriteAccess) {
      return null
    }

    function onAdd(data, cb) {
      const query = `mutation ($teamId: IdOrAlias! $role: TeamRole!) {
        ${map(data.tokens, (tokenId, index) => `token${index}: addTeamToken(teamId: $teamId, tokenId: "${tokenId}", role: $role) { id }`)}
      }`

      const variables = { teamId, role: data.role }

      update(query, variables, account, {
        reloadData: ["teamTokens", "tokenDatasetEntitlements", "token", "datasetTeamEntitlements"],
      }, cb)
    }

    return {
      title: "Add Team Tokens",
      submitText: "Add Tokens",
      track: {
        type: "team_tokens_added",
        payload: ({ tokens, role }) => ({
          tokens: map(tokens, id => tokensById[id].name),
          role,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "tokens",
        label: "Tokens",
        type: "multi",
        options: keys(optionLabels),
        optionLabels,
        validations: {
          isRequired: true,
        },
        sort: ([, lbl1], [, lbl2]) => lbl1.localeCompare(lbl2),
      }, {
        id: "role",
        label: "Role",
        type: "select",
        options: roleOptions,
        optionLabels: TEAM_ROLES,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, optionLabels, roleOptions, teamId, update, account, tokensById])

  const editForm = useMemo(() => {
    function onEdit(data, cb) {
      const query = `mutation ($teamId: IdOrAlias!, $tokenId: Id!, $role: TeamRole!) {
        updateTeamTokenRole(teamId: $teamId, tokenId: $tokenId, role: $role) { id }
      }`

      const variables = {
        teamId,
        tokenId: data.id,
        role: data.role,
      }

      update(query, variables, account, {
        reloadData: "teamTokens",
      }, cb)
    }

    return {
      title: "Edit Team Token",
      submitText: "Save",
      track: {
        type: "team_token_role_edited",
        payload: ({ id, role }) => ({
          token: tokensById[id].name,
          role,
        }),
      },
      onSubmit: onEdit,
      isEnabled: () => hasWriteAccess,
      getInitialValues: ({ teamRole }) => ({ role: teamRole }),
      fields: [{
        id: "role",
        label: "Role",
        type: "select",
        options: roleOptions,
        optionLabels: TEAM_ROLES,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [roleOptions, teamId, update, account, tokensById, hasWriteAccess])

  const deleteForm = useMemo(() => {
    function onDelete(data, cb) {
      const query = `mutation ($teamId: IdOrAlias!) {
        ${map(data, (token, index) => `token${index}: removeTeamToken(teamId: $teamId, tokenId: "${token.id}") { ok }`)}
      }`

      const variables = { teamId }

      update(query, variables, account, {
        reloadData: ["teamTokens", "tokenDatasetEntitlements", "token", "datasetTeamEntitlements"],
      }, cb)
    }

    return {
      type: "token",
      track: {
        type: "team_token_removed",
        payload: ({ name }) => ({ token: name }),
      },
      isEnabled: () => hasWriteAccess,
      renderItem: token => <li key={token.id}>{token.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, teamId, update])

  const linkTo = useCallback((token) => {
    if (role === "admin") {
      return `/${account}/home/tokens/${token.id}`
    }
  }, [account, role])
  return <div className="modules_team-tokens">
    <UpsellTabs id="team-tokens" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Team Tokens"
        idField="id"
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
        data={teamTokens}
        columns={[{
          name: "Name",
          id: "name",
          linkTo,
        }, {
          name: "Team Role",
          id: "teamRole",
          formatter: capitalize,
        }]}
      />
    </Paper>
  </div>
}

TeamTokens.propTypes = {
  account: PropTypes.string.isRequired,
  role: ROLE.isRequired,
  teamRole: ROLE,
  teamAlias: PropTypes.string.isRequired,
  teamId: UUID,
  update: PropTypes.func.isRequired,
  tokens: PropTypes.arrayOf(TOKEN),
  teamTokens: PropTypes.arrayOf(TEAM_TOKEN),
  tabs: UPSELL_TABS_SHAPE,
}
